import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, Building2 } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { useMobileAIChat } from '@/context/MobileAIChatContext';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { canOpenPortfolio } from '@/lib/constants/dashboard';
import { useBottomNavSafe } from '@/context/BottomNavContext';
import { Button } from '@/components/ui/button';

interface NavItemProps {
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon: Icon, label, isActive, onClick }) => (
  <Button variant="ghost"
    onClick={onClick}
    className={cn(
      "relative flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 transition-all duration-200",
      "active:scale-95"
    , "!h-auto md:!px-3 md:!py-1.5 rounded-none whitespace-normal", 'hover:bg-transparent')}
  >
    <Icon
      className="h-5 w-5 text-foreground transition-all duration-200"
      strokeWidth={isActive ? 2.5 : 1.5}
    />
    <span className={cn(
      "text-2xs transition-all duration-200",
      isActive ? "text-primary font-semibold" : "text-foreground font-medium"
    )}>
      {label}
    </span>
  </Button>
);

export const MobileBottomNav: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen: isAIChatOpen, openAIChat } = useMobileAIChat();
  const { businesses } = useBusinesses();
  const { user } = useAuth();
  const { currentPlan } = useSubscription();
  const isFreePlan = currentPlan?.name === 'free';
  const showPortfolio = canOpenPortfolio(currentPlan?.name, businesses.length);
  const bottomNav = useBottomNavSafe();

  // Don't render on desktop
  if (!isMobile) return null;

  // Don't render if user is not logged in
  if (!user) return null;

  // Don't render in employee portal or create business wizard
  if (location.pathname.startsWith('/employee-portal')) return null;
  if (location.pathname.startsWith('/create-business')) return null;

  // Determine active states
  const isCustomerActive = location.pathname.startsWith('/dashboard');
  const isEmployeeActive = location.pathname.startsWith('/employee-dashboard') ||
                          location.pathname.startsWith('/employee-portal');
  const isAgencyActive = location.pathname.startsWith('/portfolio');

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const isVisible = bottomNav?.isVisible ?? true;

  return (
    <div
      id="mobile-bottom-nav"
      className={cn(
        "fixed left-3 right-3 z-40 md:hidden",
        "flex items-center gap-2",
        "transition-all duration-300 ease-out",
        isVisible
          ? "bottom-3 opacity-100 translate-y-0"
          : "bottom-3 opacity-0 translate-y-[calc(100%+32px)] pointer-events-none"
      )}
      style={{
        marginBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Navigation pill */}
      <nav
        className={cn(
          "h-14 flex-shrink-0",
          "bg-card/95 backdrop-blur-xl",
          "rounded-full border",
          "shadow-xl shadow-black/15 dark:shadow-black/40"
        )}
      >
        <div className="flex items-center justify-evenly h-full px-1">
          {showPortfolio && (
          <NavItem
            icon={Building2}
            label="Portfolio"
            isActive={isAgencyActive}
            onClick={() => handleNavigate('/portfolio')}
          />
          )}
          <NavItem
            icon={Home}
            label="Home"
            isActive={isCustomerActive}
            onClick={() => handleNavigate('/dashboard')}
          />
          <NavItem
            icon={Users}
            label="Team"
            isActive={isEmployeeActive}
            onClick={() => handleNavigate('/employee-dashboard')}
          />
        </div>
      </nav>

      {/* AI input pill - just a button that opens the AI sheet */}
      {!isFreePlan && <Button variant="outline"
        onClick={openAIChat}
        className={cn(
          "flex-1 h-14 px-4",
          "bg-card/95 backdrop-blur-xl",
          "rounded-full border",
          "shadow-xl shadow-black/15 dark:shadow-black/40",
          "flex items-center gap-2",
          "transition-all duration-200",
          "active:scale-[0.98]",
          isAIChatOpen ? "border-primary" : ""
        , "md:!h-14 md:!py-0 md:!px-4", 'hover:bg-card/95')}
      >
        <img src="/media/tag-innovation-logo.png" alt="Inny" className="h-5 w-5 flex-shrink-0" />
        <span className="text-sm text-muted-foreground truncate">Ask AI...</span>
      </Button>}
    </div>
  );
};

export default MobileBottomNav;
